/**
 * Dates, heures et montants, dans la langue affichée.
 *
 * **Un seul endroit dans toute l'application.** Avant, chaque écran appelait
 * `toLocaleDateString('fr-FR', …)` avec ses propres options : quarante-cinq
 * appels, quarante-cinq occasions de diverger, et le français cloué dans le
 * code. Ici, une intention par fonction — « le jour et le mois », « la date
 * complète avec l'heure » — et la langue se lit à l'appel.
 *
 * **Rien ne passe par `Intl`.** Hermes n'embarque pas le même ICU d'un appareil
 * à l'autre, et aucun ICU ne connaît les mois malgaches : les tables de
 * `calendar.ts` rendent la même chaîne partout, ce qui est la seule garantie
 * qui vaille pour un reçu ou une date de rendez-vous.
 *
 * **Dans un composant**, ces fonctions ne suffisent pas à elles seules : elles
 * lisent la langue courante, mais ne s'y abonnent pas. Tout écran qui les
 * appelle utilise aussi `useTranslation()` — ce qu'il fait de toute façon pour
 * ses libellés — et se redessine donc au changement de langue.
 */
import { CALENDAR, CURRENCY_SUFFIX } from '@/shared/i18n/calendar';
import { currentLocale, translate } from '@/shared/i18n/instance';

/** Ce qu'on accepte de recevoir : l'ISO de l'API, un horodatage, une `Date`. */
export type DateLike = Date | string | number;

const pad2 = (value: number) => String(value).padStart(2, '0');

const names = () => CALENDAR[currentLocale()];

/**
 * Relit une valeur de date. `null` quand il n'y a rien à lire : un champ vide
 * n'est pas une erreur, et l'appelant décide de ce qu'il affiche à la place.
 */
export function toDate(value: DateLike | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const asDate = (value: DateLike): Date => toDate(value) ?? new Date(Number.NaN);

// ---------------------------------------------------------------------------
// Nombres et montants
// ---------------------------------------------------------------------------

/**
 * Un nombre entier, groupé par milliers : « 1 500 » en français et en
 * malgache, « 1,500 » en anglais.
 *
 * L'espace de groupement est insécable : un montant coupé en fin de ligne se
 * lit comme deux nombres.
 */
export function formatAmount(value: number): string {
  const rounded = Math.round(value);
  const sign = rounded < 0 ? '-' : '';
  return sign + String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, names().group);
}

/** Le montant suivi de sa devise : « 1 500 Ar ». Un prix ne s'abrège jamais. */
export function formatPrice(value: number, currency: string = CURRENCY_SUFFIX): string {
  return `${formatAmount(value)} ${currency}`;
}

// ---------------------------------------------------------------------------
// Noms du calendrier
// ---------------------------------------------------------------------------

/** Le mois en toutes lettres, aligné sur `Date#getMonth()`. */
export const monthName = (month: number): string => names().months[month] ?? '';

/** Le mois abrégé : « août », « aog. », « Aug ». */
export const monthNameShort = (month: number): string => names().monthsShort[month] ?? '';

/** Le jour de la semaine, aligné sur `Date#getDay()` — dimanche en tête. */
export const weekdayName = (day: number): string => names().weekdays[day] ?? '';

/** Le jour abrégé (« lun. ») : l'en-tête d'un calendrier. */
export const weekdayNameShort = (day: number): string => names().weekdaysShort[day] ?? '';

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** « 12/08/2026 » — la forme courte, chiffrée, jour d'abord dans les trois langues. */
export function formatDate(value: DateLike): string {
  const date = asDate(value);
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/** « 12 août 2026 » — la date qu'on lit sur un reçu ou une fiche. */
export function formatLongDate(value: DateLike): string {
  const date = asDate(value);
  return `${date.getDate()} ${monthName(date.getMonth())} ${date.getFullYear()}`;
}

/** « 12 août » — dans une liste, où l'année n'apporte rien. */
export function formatDayMonth(value: DateLike): string {
  const date = asDate(value);
  return `${date.getDate()} ${monthNameShort(date.getMonth())}`;
}

/** « 12 août », mois en toutes lettres : pour une phrase, pas pour une liste. */
export function formatDayMonthLong(value: DateLike): string {
  const date = asDate(value);
  return `${date.getDate()} ${monthName(date.getMonth())}`;
}

/** « août 2026 » — l'inscription d'un compte, une période. */
export function formatMonthYear(value: DateLike): string {
  const date = asDate(value);
  return `${monthName(date.getMonth())} ${date.getFullYear()}`;
}

/** « mercredi 12 août » — l'en-tête d'une journée d'agenda. */
export function formatWeekdayDayMonth(value: DateLike): string {
  const date = asDate(value);
  return `${weekdayName(date.getDay())} ${formatDayMonthLong(date)}`;
}

// ---------------------------------------------------------------------------
// Heures
// ---------------------------------------------------------------------------

/** « 14:30 » — l'heure seule, toujours sur 24 heures. */
export function formatClock(value: DateLike): string {
  const date = asDate(value);
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** Relie une date et une heure : « … à 14:30 », « … amin'ny 14:30 ». */
const withTime = (date: string, value: DateLike) =>
  `${date} ${names().atTime} ${formatClock(value)}`;

/** « 12/08/2026 à 14:30 » — un horodatage complet, compact. */
export const formatDateTime = (value: DateLike): string => withTime(formatDate(value), value);

/** « 12 août 2026 à 14:30 » — un horodatage qui se lit, dans un journal ou un reçu. */
export const formatLongDateTime = (value: DateLike): string =>
  withTime(formatLongDate(value), value);

/** « 12 août à 14:30 » — un rendez-vous proche, où l'année va de soi. */
export const formatDayMonthTime = (value: DateLike): string =>
  withTime(formatDayMonthLong(value), value);

// ---------------------------------------------------------------------------
// Temps écoulé
// ---------------------------------------------------------------------------

/**
 * « il y a 2 h » — l'âge d'une publication, d'un commentaire, d'un message.
 *
 * Au-delà d'une semaine, la durée cesse de vouloir dire quelque chose et cède
 * la place au jour lui-même : « il y a 34 j » ne se lit pas, « 12 juil. » si.
 *
 * Les abréviations (min, h, j) ne s'accordent pas : la valeur est passée en
 * `value` et non en `count`, ce qui évite six clés là où trois suffisent.
 */
export function formatTimeAgo(value: DateLike, now: number = Date.now()): string {
  const date = toDate(value);
  if (!date) {
    return '';
  }
  const minutes = Math.max(0, Math.round((now - date.getTime()) / 60_000));
  if (minutes < 1) {
    return translate('relative.now');
  }
  if (minutes < 60) {
    return translate('relative.minutes', { value: minutes });
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return translate('relative.hours', { value: hours });
  }
  const days = Math.round(hours / 24);
  if (days < 7) {
    return translate('relative.days', { value: days });
  }
  return formatDayMonth(date);
}
